import React from 'react';
import { Field } from 'redux-form';
import ReactQuill from 'react-quill';
import { debounce } from 'throttle-debounce';
import styled from 'styled-components';

import { InputCSS } from 'components/Shared/Forms';
import 'components/Shared/Forms/RichTextEditor/Quill/Theme';
import { RichTextEditorOptions } from 'components/Shared/Forms/RichTextEditor/RichTextEditorConstants';
import { SCAFFOLDING, TRANSITION, TYPOGRAPHY, BUTTON_PRIMARY, INPUTS, SELECTS } from 'theme';

const Group = ({ children }) => <span className="ql-formats">{children}</span>;

const Item = ({ name, title, multiple, children, ...otherProps }) => {
    const Component = multiple ? 'select' : 'button';
    return (
        <Component className={`ql-${name}`} title={title} {...otherProps}>
            {children}
        </Component>
    );
};

const CustomToolbar = ({ className }) => (
    <div className={`custom-toolbar ${className}`}>
        <Group>
            <Item name="header" title="Header" multiple />
            <Item name="font" title="Font" multiple defaultValue="font-0" />
            <Item name="size" title="Size" multiple defaultValue="font-size-14" />
        </Group>
        <Group>
            <Item name="bold" title="Bold" />
            <Item name="italic" title="Italic" />
            <Item name="underline" title="Underline " />
        </Group>
        <Group>
            <Item name="color" title="Font Color" multiple />
            <Item name="link" title="Link" />
            <Item name="list" title="Ordered List" value="ordered" />
            <Item name="list" title="Bullet List" value="bullet" />
        </Group>
        <Group>
            <Item name="align" title="Text Alignment" multiple />
        </Group>
        <Group>
            <Item name="clean" title="Clear Formatting" />
        </Group>
    </div>
);

const StyledRichTextEditor = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${SCAFFOLDING.BG};
    border-radius: ${SCAFFOLDING.BORDER_RADIUS};
    box-shadow: ${SCAFFOLDING.BOX_SHADOW.BASE};
`;

const StyledCustomToolbar = styled(CustomToolbar)`
    &.ql-toolbar.ql-snow {
        flex: 0 0 auto;
        padding: 0 ${INPUTS.PADDING.X} ${INPUTS.PADDING.X};
        border: 0;
        color: ${TYPOGRAPHY.COLOR};
        font-family: inherit;

        .ql-formats {
            margin-right: 0;
            line-height: 1;
        }

        .ql-picker,
        button {
            width: auto;
            height: 30px;
            margin-top: ${INPUTS.PADDING.X};
            margin-left: -1px;
            float: none;
            vertical-align: middle;
        }

        .ql-picker {
            color: inherit;
            font-weight: 400;

            &.ql-header {
                width: 7em;
            }

            &.ql-font {
                width: 7em;
            }

            &.ql-size {
                width: 5em;
            }
        }

        .ql-picker-label {
            padding: 0 ${INPUTS.PADDING.X};
            border-top-right-radius: ${SCAFFOLDING.BORDER_RADIUS};
            border-top-left-radius: ${SCAFFOLDING.BORDER_RADIUS};
            line-height: 26px;
            transition: color ${TRANSITION.LENGTH} ${TRANSITION.BEZIER};

            &.ql-active {
                color: inherit;
            }

            &:hover {
                color: ${TYPOGRAPHY.LINK_COLOR.BASE};
            }

            svg {
                right: 9px;
            }

            &::before {
                display: inline-block;
                width: 100%;
                padding-right: 24px;
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
                vertical-align: middle;
            }

            &::after {
                content: '';
                position: absolute;
                display: block;
                right: ${INPUTS.PADDING.X};
                bottom: -1px;
                left: ${INPUTS.PADDING.X};
                border-bottom: 1px solid;
                opacity: 0.25;
            }
        }

        .ql-picker-options {
            max-height: 25vh;
            padding: 0;
            overflow: auto;
            border-bottom-left-radius: ${SELECTS.BORDER_RADIUS};
            border-bottom-right-radius: ${SELECTS.BORDER_RADIUS};
            box-shadow: ${SELECTS.MENU.BOX_SHADOW};
        }

        .ql-picker-item {
            padding: ${SELECTS.OPTION.PADDING.Y} ${SELECTS.OPTION.PADDING.X};
            line-height: ${TYPOGRAPHY.LINE_HEIGHT};
            transition: background-color ${TRANSITION.LENGTH} ${TRANSITION.BEZIER};

            &:hover,
            &:focus {
                background-color: ${SELECTS.OPTION.HOVER_BG};
                color: inherit;
            }

            &.ql-selected {
                background-color: ${SELECTS.OPTION.ACTIVE_BG};
                color: ${SELECTS.OPTION.ACTIVE_COLOR};
            }
        }

        .ql-color-picker,
        .ql-icon-picker {
            width: auto;

            .ql-picker-label::after {
                display: none;
            }

            svg {
                width: 1.2em;
                height: 1.2em;
                vertical-align: middle;
            }
        }

        .ql-color-picker {
            .ql-picker-options {
                width: 12em;
                padding: ${SELECTS.OPTION.PADDING.X};
                white-space: normal;
            }

            .ql-picker-item {
                display: inline-block;
                width: calc(${`${100 / 7}%`} - 4px);
                height: 0;
                padding: 0 0 calc(${`${100 / 7}%`} - 4px);
                float: none;
                border: 0;
                transition: all ${TRANSITION.LENGTH} ${TRANSITION.BEZIER};

                &:hover {
                    position: relative;
                    box-shadow: ${SCAFFOLDING.BOX_SHADOW.HOVER};
                    transform: scale(1.2);
                }
            }
        }

        .ql-color-reset {
            display: block;
            margin: ${SELECTS.OPTION.PADDING.X} -${SELECTS.OPTION.PADDING.X} -${SELECTS.OPTION.PADDING.X};
            padding: ${SELECTS.OPTION.PADDING.X} ${INPUTS.PADDING.X};
            clear: both;
            border-top: 1px solid ${INPUTS.BORDER.BASE};
            cursor: pointer;
            font-size: 0.9em;
            line-height: ${TYPOGRAPHY.LINE_HEIGHT};
            text-align: center;
        }

        button {
            margin-left: -1px;
            padding: 0 ${INPUTS.PADDING.X};
            border: 1px solid transparent;
            color: inherit;
            transition: color ${TRANSITION.LENGTH} ${TRANSITION.BEZIER};

            &:hover {
                color: ${TYPOGRAPHY.LINK_COLOR.BASE};
            }

            svg {
                width: 1.2em;
                height: 1.2em;
            }

            &.ql-active {
                background-color: #eee;
                color: inherit;
            }
        }

        .ql-stroke {
            stroke: currentColor !important;
        }

        .ql-fill {
            fill: currentColor !important;
        }
    }
`;

// Always work in deltas rather than html
class CustomReactQuill extends ReactQuill {
    setEditorContents = (editor, value) => {
        const sel = editor.getSelection();
        if (typeof value === 'string') {
            editor.clipboard.dangerouslyPasteHTML(value || '');
        } else if (value.delta) {
            editor.setContents(value.delta);
        }
        if (sel) this.setEditorSelection(editor, sel);
    };
}

const StyledReactQuill = styled(CustomReactQuill)`
    border-top: 1px solid ${INPUTS.BORDER.BASE};
    border-bottom-left-radius: ${SCAFFOLDING.BORDER_RADIUS};
    border-bottom-right-radius: ${SCAFFOLDING.BORDER_RADIUS};

    .ql-container {
        font-family: ${RichTextEditorOptions.DefaultFontFamily};
        font-size: ${RichTextEditorOptions.DefaultFontSize};

        &.ql-snow {
            border: 0;
        }
    }

    .ql-snow .ql-editor {
        height: 40vh;
        min-height: 20em;
        padding: ${`${parseFloat(INPUTS.PADDING.X) * 2}em`};
    }

    .ql-snow .ql-tooltip {
        padding: ${INPUTS.PADDING.X} ${`${parseFloat(INPUTS.PADDING.X) * 2}em`};
        border: 0;
        border-radius: ${SCAFFOLDING.BORDER_RADIUS};
        box-shadow: ${SCAFFOLDING.BOX_SHADOW.HOVER};

        &:before {
            content: 'URL:';
        }

        &.ql-editing {
            input[type='text'] {
                ${InputCSS}
                display: inline-block;
                width: 15em;
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
            }

            a.ql-action {
                display: inline-block;
                margin-left: 0;
                padding-left: ${BUTTON_PRIMARY.PADDING.X};
                padding-right: ${BUTTON_PRIMARY.PADDING.X};
                background-color: ${BUTTON_PRIMARY.BG.BASE};
                border-top-right-radius: ${BUTTON_PRIMARY.BORDER_RADIUS};
                border-bottom-right-radius: ${BUTTON_PRIMARY.BORDER_RADIUS};
                box-shadow: ${BUTTON_PRIMARY.BOX_SHADOW.BASE};
                color: ${BUTTON_PRIMARY.COLOR.BASE};
                line-height: ${INPUTS.HEIGHT};
                vertical-align: middle;
                transition: ${BUTTON_PRIMARY.TRANSITION};

                &:hover,
                &:focus {
                    background-color: ${BUTTON_PRIMARY.BG.HOVER};
                    color: ${BUTTON_PRIMARY.COLOR.HOVER};
                    box-shadow: ${BUTTON_PRIMARY.BOX_SHADOW.HOVER};
                    text-decoration: none;
                }

                &:active {
                    box-shadow: ${BUTTON_PRIMARY.BOX_SHADOW.ACTIVE};
                }
            }
        }

        a.ql-action::after,
        a.ql-remove::before {
            margin: 0;
            padding: 0;
            border: 0;
        }

        a {
            flex: 0 0 auto;
            color: ${TYPOGRAPHY.LINK_COLOR.BASE};
            line-height: ${INPUTS.HEIGHT};
            transition: color ${TRANSITION.LENGTH} ${TRANSITION.BEZIER};

            &:hover,
            &:focus {
                color: ${TYPOGRAPHY.LINK_COLOR.HOVER};
                text-decoration: underline;
            }
        }

        a.ql-preview {
            flex: 1 1 auto;
            max-width: none;
            font-style: italic;
            text-decoration: underline;
        }

        a.ql-action {
            margin-left: ${`${parseFloat(INPUTS.PADDING.X) * 2}em`};
        }

        a.ql-remove {
            position: relative;
            margin-left: ${`${parseFloat(INPUTS.PADDING.X) * 2}em`};

            &::after {
                content: '';
                display: inline-block;
                position: absolute;
                top: 50%;
                left: -${INPUTS.PADDING.X};
                height: 1em;
                margin-top: -0.5em;
                border-left: 1px solid ${INPUTS.BORDER.BASE};
                vertical-align: middle;
                pointer-events: none;
            }
        }
    }
`;

class RichTextEditorWrapper extends React.Component {
    constructor(props) {
        super(props);
        this.handleOnChange = debounce(250, (value, delta, source, editor) => {
            // Trim out base64 images pasted into the text editor so they don't clog up the database.
            var base64ImageRegex = /data:image\/[a-zA-Z]+;base64,[^"]*?/gi;

            let trimmedValue = value;
            try {
                trimmedValue = trimmedValue.replace(base64ImageRegex, '');
            } catch (err) {}

            let trimmedDelta = editor.getContents();
            try {
                if (trimmedDelta && trimmedDelta.ops) {
                    for (var i = 0; i < trimmedDelta.ops.length; i++) {
                        var op = trimmedDelta.ops[i];
                        if (op && op.insert && op.insert.image && base64ImageRegex.test(op.insert.image)) {
                            trimmedDelta.ops.splice(i, 1);
                        }
                    }
                }
            } catch (err) {}

            this.props.input.onChange({
                html: trimmedValue === '<p><br></p>' ? null : trimmedValue,
                delta: trimmedDelta,
            });
        }).bind(this);
    }

    render() {
        const { fontColor, backgroundColor } = this.props;
        return (
            <StyledRichTextEditor>
                <StyledCustomToolbar />
                <StyledReactQuill
                    theme="snazzy"
                    value={this.props.input.value}
                    placeholder={this.props.placeholder}
                    onChange={this.handleOnChange}
                    modules={{
                        toolbar: '.custom-toolbar',
                    }}
                    style={{
                        color: fontColor,
                        backgroundColor,
                    }}
                />
            </StyledRichTextEditor>
        );
    }
}

const QuillRichTextEditorField = (props) => <Field component={RichTextEditorWrapper} {...props} />;

export default QuillRichTextEditorField;
